"use client";

import React, {
  createContext,
  forwardRef,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState,
} from "react";

import { EnumViewMode } from "@/enums/view-mode";
import { ViewType } from "@/types/search.types";

// Determine the context
interface IViewContext {
  viewMode: ViewType;
  handleChangeViewMode: (view: EnumViewMode) => void;
}

const VIEW_KEY = "avezor_view_preference";

// Create context
const ViewContext = createContext<IViewContext | undefined>(undefined);

// Hook for use of context
export const useViewMode = () => {
  const context = useContext(ViewContext);
  if (!context) {
    // Return a fallback context for SSR/build time
    return {
      viewMode: EnumViewMode.GRID,
      handleChangeViewMode: () => {}, // No-op during SSR
    };
  }
  return context;
};

// Context provider
export const ViewProvider = forwardRef<HTMLDivElement, { children: ReactNode }>(
  ({ children }, ref) => {
    const [viewMode, setViewMode] = useState<ViewType>(EnumViewMode.GRID);

    useEffect(() => {
      const _viewMode = localStorage.getItem(VIEW_KEY) as ViewType;
      if (_viewMode) {
        setViewMode(_viewMode);
      }
    }, []);

    const handleChangeViewMode = useCallback((type: ViewType) => {
      setViewMode(type);
      localStorage.setItem(VIEW_KEY, type);
    }, []);

    return (
      <ViewContext.Provider value={{ viewMode, handleChangeViewMode }}>
        <div ref={ref}>{children}</div>
      </ViewContext.Provider>
    );
  },
);

ViewProvider.displayName = "ViewProvider";
